// src/pages/entit/stage.js
import React, { useMemo, useState } from "react";
import { Container, Row, Col, Form, Table } from "react-bootstrap";
import ReactECharts from "echarts-for-react";
import stageData from "../../assets/data/stage.json";
import "../../App.css";

// ⭐ NEW: import export + datenstand
import { exportToExcel } from "../../utils/exportExcel";
import { DATENSTAND } from "../../config/constants";
import { UICC_COLORS } from "../../config/colors";

// --- formatters ---
const nfInt = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });
const nfPct1 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const fmtInt = (v) => (v == null ? "–" : nfInt.format(v));
const fmtPct = (v) => (v == null ? "–" : nfPct1.format(v));

// Canonical order
const STAGE_ORDER = ["0", "I", "II", "III", "IV", "X"];


const STAGING_RULES = [
  {
    system: "Ann Arbor",
    matchIncludes: ["lymphom", "non-hodgkin", "hodgkin"],
    allowed: ["I", "II", "III", "IV", "X"],
    labelFor: (stage) => `Ann Arbor ${stage}`,
  },  
  {
    system: "AJCC - Amercian Joint Committee on Cancer",
    matchIncludes: ["malignes melanom der haut" ],
    allowed: ["I", "II", "III", "IV", "X"],
    labelFor: (stage) => `AJCC ${stage}`,
  },
  {
    system: "ISS - International Staging System",
    matchIncludes: ["myelom", "plasma", "multiples myelom"],
    allowed: ["I", "II", "III", "X"],
    labelFor: (stage) => `ISS ${stage}`,
  },
  {
    system: "Binet",
    matchIncludes: ["cll", "chronische lymphatische leukämie", "leukämie"],
    allowed: ["I", "II", "III", "X"],
    labelFor: (stage) => {
      const map = { I: "A", II: "B", III: "C", X: "X" };
      const letter = map[stage];
      return letter ? `Binet ${letter}` : `Binet ${stage}`;
    },
  },
  {
    system: "UICC - Stadium",
    matchIncludes: [],
    allowed: STAGE_ORDER,
    labelFor: (stage) => `UICC ${stage}`,
  },
];

function pickStagingSystem(entit) {
  const name = String(entit || "").toLowerCase();
  for (const rule of STAGING_RULES) {
    if (rule.matchIncludes.some((s) => name.includes(s))) return rule;
  }
  return STAGING_RULES[STAGING_RULES.length - 1];
}

const Stage = () => {
  const years = useMemo(
    () => [...new Set(stageData.map((d) => d.diayear))].sort((a, b) => (a > b ? 1 : -1)),
    []
  );
  const entits = useMemo(
    () => [...new Set(stageData.map((d) => d.entit))].sort((a, b) => a.localeCompare(b, "de")),
    []
  );

const [year, setYear] = useState(
  String(years[years.length - 1] ?? "")
);
  const [entit, setEntit] = useState(entits[0] ?? "");

  const stagingRule = useMemo(() => pickStagingSystem(entit), [entit]);

  const filtered = useMemo(
    () => stageData.filter((d) => String(d.diayear) === String(year) && d.entit === entit),
    [year, entit]
  );

  const maleRows = useMemo(() => filtered.filter((d) => d.SEX === "M"), [filtered]);
  const femaleRows = useMemo(() => filtered.filter((d) => d.SEX === "W"), [filtered]);

  const totalM = useMemo(() => maleRows.reduce((s, r) => s + (Number(r.n) || 0), 0), [maleRows]);
  const totalW = useMemo(() => femaleRows.reduce((s, r) => s + (Number(r.n) || 0), 0), [femaleRows]);
  const totalAll = totalM + totalW;

  const presentStages = useMemo(() => {
    const s = new Set();
    filtered.forEach((r) => {
      if (r.uicc != null) s.add(String(r.uicc));
    });
    return s;
  }, [filtered]);

  const legendStages = useMemo(() => {
    const allowed = stagingRule.allowed || STAGE_ORDER;
    const keep = allowed.filter((st) => presentStages.has(st));
    return keep.length ? keep : allowed;
  }, [stagingRule, presentStages]);

  const pieDataFor = (rows) =>
    rows
      .filter((r) => r.pct != null && r.uicc != null)
      .sort((a, b) => STAGE_ORDER.indexOf(String(a.uicc)) - STAGE_ORDER.indexOf(String(b.uicc)))
      .map((r) => {
        const stageKey = String(r.uicc);
        return {
          name: stagingRule.labelFor(stageKey),
          rawStage: stageKey,
          value: Number(r.pct),
          n: Number(r.n),
          color: UICC_COLORS[stageKey] || "#bbb",
        };
      });

  const malePie = useMemo(() => pieDataFor(maleRows), [maleRows, stagingRule]);
  const femalePie = useMemo(() => pieDataFor(femaleRows), [femaleRows, stagingRule]);

  const basePieOption = {
    tooltip: {
      trigger: "item",
      backgroundColor: "#000",
      borderWidth: 0,
      textStyle: { color: "#fff", fontSize: 12 },
      confine: true,
      formatter: (p) =>
        `<div style="text-align:left;line-height:1.3">
          <div style="font-weight:600;margin-bottom:4px">${p.seriesName} – ${p.name}</div>
          <div>Anzahl: ${fmtInt(p.data?.n)}</div>
          <div>Anteil: ${fmtPct(p.value)}%</div>
        </div>`,
    },
    legend: { show: false },
    series: [],
  };

  const menOption = {
    ...basePieOption,
    series: [
      {
        name: "Männer",
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        minShowLabelAngle: 2,
        label: { show: true, formatter: (p) => `${p.name}: ${fmtPct(p.value)}%`, fontSize: 12 },
        labelLine: { show: true, length: 12, length2: 8 },
        data: malePie.map((d) => ({
          ...d,
          itemStyle: { color: d.color, borderWidth: 1, borderColor: "#fff" },
        })),
      },
    ],
  };

  const womenOption = {
    ...basePieOption,
    series: [
      {
        name: "Frauen",
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        minShowLabelAngle: 2,
        label: { show: true, formatter: (p) => `${p.name}: ${fmtPct(p.value)}%`, fontSize: 12 },
        labelLine: { show: true, length: 12, length2: 8 },
        data: femalePie.map((d) => ({
          ...d,
          itemStyle: { color: d.color, borderWidth: 1, borderColor: "#fff" },
        })),
      },
    ],
  };

  const tableRows = useMemo(() => {
    const map = new Map();

    const add = (row, sexKey) => {
      const key = String(row.uicc);
      if (!map.has(key)) {
        map.set(key, {
          raw: key,
          disp: stagingRule.labelFor(key),
          n_m: null,
          n_w: null,
          pct_m: null,
          pct_w: null,
        });
      }
      const rec = map.get(key);
      if (sexKey === "M") {
        rec.n_m = Number(row.n);
        rec.pct_m = Number(row.pct);
      } else {
        rec.n_w = Number(row.n);
        rec.pct_w = Number(row.pct);
      }
    };

    maleRows.forEach((r) => add(r, "M"));
    femaleRows.forEach((r) => add(r, "W"));

    const arr = [...map.values()].map((r) => {
      const n_g = (r.n_m || 0) + (r.n_w || 0);
      const pct_g = totalAll > 0 ? (n_g / totalAll) * 100 : null;
      return { ...r, n_g, pct_g };
    });

    arr.sort((a, b) => STAGE_ORDER.indexOf(a.raw) - STAGE_ORDER.indexOf(b.raw));
    return arr;
  }, [maleRows, femaleRows, totalAll, stagingRule]);

  const footer = useMemo(() => {
    const n_g = totalAll;
    const n_m = totalM;
    const n_w = totalW;
    return {
      n_g,
      n_m,
      n_w,
      pct_g: 100,
      pct_m: totalM > 0 ? 100 : null,
      pct_w: totalW > 0 ? 100 : null,
    };
  }, [totalAll, totalM, totalW]);

  const LegendStage = () => (
    <div style={{ fontSize: 12, margin: "6px 0 10px" }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Klassifikation: {stagingRule.system}</div>
      {legendStages.map((s) => (
        <span key={s} style={{ marginRight: 16 }}>
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              borderRadius: 3,
              backgroundColor: UICC_COLORS[s],
              marginRight: 6,
              verticalAlign: "middle",
            }}
          />
          {stagingRule.labelFor(s)}
        </span>
      ))}
    </div>
  );

  // ⭐ ADD: Excel export
  const handleExport = () => {
    const excelData = tableRows.map((r) => ({
      Stadium: r.disp,
      Gesamt: fmtInt(r.n_g),
      Männer: fmtInt(r.n_m),
      Frauen: fmtInt(r.n_w),
      "Gesamt (%)": fmtPct(r.pct_g),
      "Männer (%)": fmtPct(r.pct_m),
      "Frauen (%)": fmtPct(r.pct_w),
    }));

    exportToExcel({
      filename: "stadium",
      sheetName: "Stadium",
      data: excelData,
      metadata: [
        ["Stadium"],
        ["Krebsregister Sachsen-Anhalt https://www.kr-lsa.de"],
        [`Datenstand: ${DATENSTAND}`],
        [`Export am: ${new Date().toLocaleString("de-DE")}`],
      ],
    });
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Stadium</h2>
      <div className="section-title-underline"></div>

      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Krebserkrankungen werden in verschiedene Stadien unterteilt. Für solide Tumoren wird bei der Diagnose
          das UICC-Stadium (TNM) bestimmt. Für systemische Erkrankungen bestehen andere Klassifikationssysteme,
          z.&nbsp;B. Ann Arbor (Lymphome), ISS (Multiples Myelom) oder Binet (CLL).
        </p>
        <p>
          Die gewählte Klassifikation richtet sich automatisch nach der ausgewählten Entität. Die Anteile sind
          prozentual dargestellt; absolute Fallzahlen stehen in der Tabelle.
        </p>
      </div>

      {/* ⭐ EXPORT BUTTON */}
      <button onClick={handleExport} className="top-btn mb-3" >
        📥 Daten als Excel exportieren
      </button>

      {/* Dropdowns */}
      <div className="d-flex align-items-center gap-3 mb-4" style={{ maxWidth: 700 }}>
        <Form.Group controlId="entitSelect" className="flex-fill">
          <Form.Label>Krebs-Auswahl:</Form.Label>
          <Form.Select value={entit} onChange={(e) => setEntit(e.target.value)}>
            {entits.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="yearSelect" className="flex-fill">
          <Form.Label>Jahr:</Form.Label>
          <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {/* Pies */}
      <Row className="mb-4">
        <Col md={6}>
          <h5>Männer</h5>
          <LegendStage />
          <ReactECharts option={menOption} style={{ width: "100%", height: 420 }} />
        </Col>
        <Col md={6}>
          <h5>Frauen</h5>
          <LegendStage />
          <ReactECharts option={womenOption} style={{ width: "100%", height: 420 }} />
        </Col>
      </Row>

      {/* Table */}
      <Table
        responsive
        className="zeitverlauf-table text-center align-middle"
        style={{ maxWidth: "1000px" }}
      >
        <thead>
          <tr>
            <th className="text-start">Stadium ({stagingRule.system})</th>
            <th colSpan={3}>Anzahl</th>
            <th colSpan={3}>Anteil in %</th>
          </tr>
          <tr>
            <th className="text-start"></th>
            <th>Gesamt</th>
            <th>Männer</th>
            <th>Frauen</th>
            <th>Gesamt</th>
            <th>Männer</th>
            <th>Frauen</th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((r) => (
            <tr key={`stage-${r.raw}`}>
              <td className="text-start">{r.disp}</td>
              <td>{fmtInt(r.n_g)}</td>
              <td>{fmtInt(r.n_m)}</td>
              <td>{fmtInt(r.n_w)}</td>
              <td>{fmtPct(r.pct_g)}</td>
              <td>{fmtPct(r.pct_m)}</td>
              <td>{fmtPct(r.pct_w)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="table-secondary">
            <td className="text-start fw-bold">Gesamt</td>
            <td className="fw-bold">{fmtInt(footer.n_g)}</td>
            <td className="fw-bold">{fmtInt(footer.n_m)}</td>
            <td className="fw-bold">{fmtInt(footer.n_w)}</td>
            <td className="fw-bold">{fmtPct(footer.pct_g)}</td>
            <td className="fw-bold">{fmtPct(footer.pct_m)}</td>
            <td className="fw-bold">{fmtPct(footer.pct_w)}</td>
          </tr>
        </tfoot>
      </Table>
    </Container>
  );
};

export default Stage;

